/**
 * Shared platform utilities
 * Provides common functionality for both mobile and desktop
 */

// Platform-specific constants
export const PLATFORM_CONFIG = {
  mobile: {
    touchTargetSize: 44, // Minimum touch target size in pixels
    scrollThreshold: 50, // Pull-to-refresh threshold
    hapticIntensity: 'light', // Default haptic intensity
    animationDuration: 300, // Animation duration in ms
  },
  desktop: {
    keyboardShortcutDelay: 100, // Delay for keyboard shortcuts
    dragThreshold: 5, // Minimum drag distance to trigger
    hoverDelay: 200, // Hover delay for tooltips
    animationDuration: 200, // Faster animations for desktop
  }
};

// Platform detection utilities
export const getPlatformConfig = (platform) => {
  return PLATFORM_CONFIG[platform] || PLATFORM_CONFIG.desktop;
};

// Feature detection utilities
export const supportsTouch = () => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

export const supportsHaptic = () => {
  return 'vibrate' in navigator;
};

export const supportsKeyboard = () => {
  return !supportsTouch() || window.innerWidth > 768;
};

// Performance utilities
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export const throttle = (func, limit) => {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

// Platform-specific event handlers
export const createPlatformEventHandlers = (platform) => {
  const config = getPlatformConfig(platform);
  
  return {
    // Touch handlers for mobile
    handleTouchStart: (callback) => (e) => {
      if (platform === 'mobile') {
        e.preventDefault();
        callback(e);
      }
    },
    
    // Keyboard handlers for desktop
    handleKeyDown: (callback) => (e) => {
      if (platform === 'desktop') {
        callback(e);
      }
    },
    
    // Mouse handlers for desktop
    handleMouseEnter: (callback) => (e) => {
      if (platform === 'desktop') {
        setTimeout(() => callback(e), config.hoverDelay);
      }
    },
    
    // Drag handlers
    handleDragStart: (callback) => (e) => {
      if (platform === 'desktop') {
        callback(e);
      }
    }
  };
};
